import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from "react";

/** Everything inside the panel that can hold focus, for the Tab cycle. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * An accessible dialog (spec §5.1): `role="dialog"`, `aria-modal`, labelled by
 * its title, focus trapped inside and returned to the opener on unmount.
 *
 * Rendered in place rather than through a portal — `ConfirmProvider` mounts it
 * at the app root and `position: fixed` puts it over the shell regardless.
 */
export function Modal({
  title,
  onClose,
  children,
  actions,
  width = 480,
  dismissible = true,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  width?: number;
  /**
   * Whether Escape and a backdrop click may close the dialog. Set this false
   * while an action is in flight: disabling the Cancel button is not enough on
   * its own, because both of those paths bypass it entirely and unmount the
   * dialog while the request carries on. The work still lands — queue access
   * still changes, a new client is still created and navigated to — but it
   * lands after the operator was shown what looked like a cancellation.
   */
  dismissible?: boolean;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();
    return () => {
      const opener = openerRef.current;
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  /** Escape closes; Tab is wrapped so focus cannot leave the dialog. */
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      // Swallowed either way: an Escape that reaches the shell behind a busy
      // dialog would be acted on by whatever is underneath it.
      e.stopPropagation();
      if (dismissible) onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (nodes.length === 0) {
      e.preventDefault();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="modal" onKeyDown={onKeyDown}>
      <div
        className="modal__backdrop"
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className="modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ width }}
      >
        <h2 id={titleId} className="modal__title">{title}</h2>
        <div className="modal__body">{children}</div>
        {actions ? <div className="modal__actions">{actions}</div> : null}
      </div>
    </div>
  );
}
